import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * BuddyAvanzaLegalFooter — Global legal footer (FASE LEGAL-1 + PRE-PUBLISH).
 *
 * Discreet, Light-Theme compatible, responsive.
 * Consumed by: Landing, Checkout, Login B2C, and B2C authenticated env.
 *
 * Consumes GET /api/avanzo-b2c/legal-config to render provider disclosure.
 */
export default function BuddyAvanzaLegalFooter({ dense = false, contactEmail: contactEmailOverride }) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/avanzo-b2c/legal-config`);
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setConfig(j);
      } catch (_) { /* silent — static fallback below */ }
    })();
    return () => { alive = false; };
  }, []);

  const contactEmail = contactEmailOverride || config?.contact_email || "contacto@qhubglobal.com";
  const provider = config?.provider || {
    display_name:   "QHUB GLOBAL S.A. de C.V.",
    nit:            "0623-150526-119-8",
    location:       "San Salvador, El Salvador",
    phone:          "+503 6058 8169",
    phone_tel_link: "tel:+50360588169",
    email:          "contacto@qhubglobal.com",
  };

  const links = [
    { href: "/buddy-avanza/terminos",            label: "Términos y Condiciones", testid: "legal-footer-terms" },
    { href: "/buddy-avanza/privacidad",          label: "Privacidad",             testid: "legal-footer-privacy" },
    { href: "/buddy-avanza/privacidad-estudiantes", label: "Privacidad (estudiantes)", testid: "legal-footer-student-privacy" },
    { href: "/buddy-avanza/uso-responsable",     label: "Uso Responsable",        testid: "legal-footer-aup" },
    { href: "/buddy-avanza/inteligencia-artificial", label: "Inteligencia Artificial", testid: "legal-footer-ai" },
  ];

  return (
    <div
      data-testid="buddy-avanza-legal-footer"
      className={`w-full border-t border-slate-200 bg-white/70 backdrop-blur-sm ${dense ? "py-4" : "py-6"}`}
    >
      <div className="max-w-6xl mx-auto px-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between text-[12px] text-slate-600">
        <div className="flex flex-col gap-1">
          <div className="text-slate-500">
            © 2026 {provider.display_name} · BUDDY AVANZA
          </div>
          <div className="text-[11px] text-slate-500 leading-relaxed" data-testid="provider-disclosure">
            <span data-testid="provider-display-name" className="font-semibold text-slate-600">{provider.display_name}</span>
            <span className="mx-1 text-slate-300">·</span>
            <span>NIT <span data-testid="provider-nit">{provider.nit}</span></span>
            <span className="mx-1 text-slate-300">·</span>
            <span data-testid="provider-location">{provider.location}</span>
            <span className="mx-1 text-slate-300">·</span>
            <a
              href={provider.phone_tel_link}
              data-testid="provider-phone"
              className="hover:text-slate-900 hover:underline underline-offset-2 transition-colors"
            >{provider.phone}</a>
            <span className="mx-1 text-slate-300">·</span>
            <a
              href={`mailto:${provider.email || contactEmail}`}
              data-testid="provider-email"
              className="hover:text-slate-900 hover:underline underline-offset-2 transition-colors"
            >{provider.email || contactEmail}</a>
          </div>
        </div>
        <nav className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={l.testid}
              className="hover:text-slate-900 hover:underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6] rounded"
            >
              {l.label}
            </Link>
          ))}
          <a
            href={`mailto:${contactEmail}`}
            data-testid="legal-footer-contact"
            className="hover:text-slate-900 hover:underline underline-offset-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cf6] rounded"
          >
            Contacto
          </a>
        </nav>
      </div>
    </div>
  );
}
